import type { ComponentProps, CSSProperties } from 'preact';
import type { Line, LineRef } from '../../../arcticapi/types';
import styles from './LineRoundel.module.css';
import { SvgIcon } from '../Icon/SvgIcon';
import type { SvgName } from '../Icon/SvgRegistry';
import { getTransportModeIcon } from '../../../transportModes';

type RoundelLine =
  | Pick<Line, 'name' | 'colors' | 'mode'>
  | Pick<LineRef, 'name' | 'colors' | 'mode'>;

type LineRoundelVariant = 'default' | 'largeLink';

export interface LineRoundelProps extends Omit<
  ComponentProps<'span'>,
  'children' | 'className'
> {
  line: RoundelLine;
  variant?: LineRoundelVariant;
  iconName?: SvgName;
}

/**
 * Compact line badge for service number and transport mode.
 *
 * Intended for reusable route labeling across the widget, with an optional
 * larger linked style for line navigation.
 */
export function LineRoundel({
  line,
  iconName,
  style,
  variant = 'default',
  ...props
}: LineRoundelProps) {
  const backgroundColor = line.colors?.background ?? '#335E8D';
  const textColor = line.colors?.foreground ?? '#FFFFFF';
  const styleOverrides =
    style && typeof style === 'object' && !Array.isArray(style)
      ? (style as CSSProperties)
      : {};
  const iconColor =
    typeof styleOverrides.color === 'string' ? styleOverrides.color : textColor;
  const classNames = [
    styles.lineRoundel,
    variant === 'largeLink' ? styles.largeLink : '',
  ]
    .filter(Boolean)
    .join(' ');
  const resolvedIconName = iconName ?? getTransportModeIcon(line.mode);

  return (
    <span
      data-component="lineRoundel"
      className={classNames}
      {...props}
      style={{
        backgroundColor,
        color: textColor,
        ...styleOverrides,
      }}
    >
      <SvgIcon
        size={24}
        strokeWidth="0"
        fill={iconColor}
        name={resolvedIconName}
      />
      <span className={styles.lineName}>{line.name}</span>
    </span>
  );
}
